import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  DestroyRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatCard } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { Router } from '@angular/router';
import { finalize, take, tap } from 'rxjs';
import { FormInlineErrorComponent } from '@k12/forms';
import { SubTask, Task, TaskTypeAction, Address, ProgramType } from '@k12/models';
import { SchoolsService } from '@k12/school-services';
import { LoadingSpinnerService } from '@k12/app-services';
@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-award-endorsement-form',
  imports: [
    ReactiveFormsModule,
    MatInput,
    MatCard,
    MatButton,
    MatRadioGroup,
    MatRadioButton,
    MatFormField,
    MatSelect,
    MatOption,
    FormInlineErrorComponent
],
  templateUrl: './award-endorsement-form.component.html',
  styleUrl: './award-endorsement-form.component.scss'
})
export class AwardEndorsementFormComponent implements OnInit {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly schoolsService = inject(SchoolsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly spinnerService = inject(LoadingSpinnerService);

  @Input() task: Task | null;
  @Input() taskTypeAction: TaskTypeAction | null;
  @Input() taskAction: SubTask | null;
  @Input() programType: ProgramType = ProgramType.OS;
  @Output() submitFormData: EventEmitter<string> = new EventEmitter<string>();

  protected viewMode: 'readonly' | 'edit' = 'edit';
  protected form: FormGroup;
  protected ProgramType = ProgramType;
  private rosterParentFirstName: string | null = null;
  private rosterParentLastName: string | null = null;

  public ngOnInit(): void {
    this.assertRequiredInputs();
    this.initializeForm();
    this.lockReadOnlyFields();
    this.registerSubscriptions();
    this.restoreFormDataIfPresent();
    this.loadRosterDetails();
    this.applyReadOnlyStatus();
  }

  public ProgramTypeName(): string {
    switch (this.programType) {
      case ProgramType.OS:
        return 'Opportunity Scholarship';
      case ProgramType.ESA:
        return 'ESA+';
    }
  }

  private assertRequiredInputs(): void {
    if (!this.task || !this.taskTypeAction || !this.taskAction) {
      throw new Error('Task, TaskTypeAction, and TaskAction are required inputs');
    }

    if (!this.taskAction.id) {
      throw new Error('TaskAction must have an id');
    }
  }

  private initializeForm(): void {
    this.form = this.formBuilder.group({
      parentGuardianName: [null, Validators.required],
      studentName: [null],
      studentProgramId: [null],
      permanentAddress: [null, Validators.required],
      emailAddress: [null, [Validators.required, Validators.email]],
      schoolName: [null],
      studentRequiredTuitionAndFees: [null],
      semester: [null],
      termStartDate: [null],
      termTuitionAndFees: [null],
      endorsementDecision: [null, Validators.required],
      refusalReason: [null],
      refusalReasonOther: [null],
      parentFirstName: [null, Validators.required],
      parentLastName: [null, Validators.required],
      studentEnrollmentId: [null],
      awardAmount: [null]
    });
  }

  private lockReadOnlyFields(): void {
    this.form.get('parentGuardianName')?.disable();
    this.form.get('studentName')?.disable();
    this.form.get('studentProgramId')?.disable();
    this.form.get('permanentAddress')?.disable();
    this.form.get('emailAddress')?.disable();
    this.form.get('schoolName')?.disable();
    this.form.get('semester')?.disable();
    this.form.get('termStartDate')?.disable();
    this.form.get('studentRequiredTuitionAndFees')?.disable();
    this.form.get('termTuitionAndFees')?.disable();
    this.form.get('awardAmount')?.disable();
  }

  private registerSubscriptions(): void {
    this.form.get('endorsementDecision')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((decision) => {
        this.handleEndorsementDecisionChange(decision);
        this.cdr.markForCheck();
      });

    this.form.get('refusalReason')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reason) => {
        this.handleRefusalReasonChange(reason);
        this.cdr.markForCheck();
      });
  }

  private loadRosterDetails(): void {
    const studentEnrollmentId = this.form.get('studentEnrollmentId')?.value;
    
    if (!studentEnrollmentId) return;

    this.spinnerService.showSpinner();
    this.schoolsService
      .getStudentRoster(studentEnrollmentId)
      .pipe(
        take(1),
        tap((response) => {
          const roster = response.entity;
          if (roster) {
            const permanentAddress = this.getAddressWithLineBreaks((roster.studentAddress ?? undefined) as Address);

            // Store roster parent names for validation
            this.rosterParentFirstName = roster.parentFirstName || null;
            this.rosterParentLastName = roster.parentLastName || null;
            const academicYearCost = roster.academicYearIndividualCostTotal ?? 0;
            const defaultCost = roster.defaultStandardCost ?? 0;
            this.form.patchValue(
              {
                studentProgramId: roster.pidNumber || 'N/A',
                schoolName: roster.schoolName || 'N/A',
                parentGuardianName: `${roster.parentFirstName} ${roster.parentLastName}`,
                studentName: `${roster.studentFirstName} ${roster.studentLastName}`,
                permanentAddress: permanentAddress,
                emailAddress: roster.parentEmailAddress || 'N/A',
                studentRequiredTuitionAndFees: academicYearCost > 0 ? academicYearCost : defaultCost,
                termStartDate: roster.termStartDate || null,
                termTuitionAndFees: (roster.termCost ?? 0) > 0 ? roster.termCost : roster.defaultStandardCost ?? 0,
              },
              { emitEvent: false }
            );

            // Update validators after roster data is loaded, but only if endorsement decision is set
            const endorsementDecision = this.form.get('endorsementDecision')?.value;
            if (endorsementDecision) {
              this.updateParentNameValidators();
            }
            
            this.cdr.markForCheck();
          }
        }),
        finalize(() => {
          this.spinnerService.hideSpinner();
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }
  
  private getAddressWithLineBreaks(address?: Address | undefined): string {
    if (!address) {
      return 'N/A';
    }

    const addressParts: string[] = [];

    if (address.address1) addressParts.push(address.address1);
    if (address.address2) addressParts.push(address.address2);

    const cityStateZip = this.formatCityStateZip(address);
    if (cityStateZip) {
      addressParts.push(cityStateZip);
    }

    const addressString = addressParts.join(', ');
    return addressString || 'N/A';
  }

  private formatCityStateZip(address: Address): string {
    const parts: string[] = [];

    if (address.city) {
      parts.push(address.city);
    }

    if (address.state) {
      let stateName: string | undefined;
      if (typeof address.state === 'string') {
        stateName = address.state;
      } else if (typeof address.state === 'object' && address.state !== null && 'name' in address.state) {
        const stateObj = address.state as { name?: string };
        if (typeof stateObj.name === 'string') {
          stateName = stateObj.name;
        }
      }
      if (stateName) {
        const stateZip = address.zip ? `${stateName} ${address.zip}` : stateName;
        parts.push(stateZip);
      } else if (address.zip) {
        parts.push(address.zip);
      }
    } else if (address.zip) {
      parts.push(address.zip);
    }

    return parts.join(', ');
  }

  private restoreFormDataIfPresent(): void {
    const formData = this.taskAction?.formData;
    if (!formData) return;

    const parsed = this.safeParseJson(formData);
    if (!parsed || typeof parsed !== 'object') return;

    this.form.patchValue(parsed as Record<string, unknown>, { emitEvent: false });
  }

  private applyReadOnlyStatus(): void {
    const statusId = this.taskAction?.taskStatusId;
    if (statusId !== 1 && statusId !== 4) {
      this.form.disable();
    } else {
      // Ensure editable fields are enabled when form is in edit mode
      this.form.get('endorsementDecision')?.enable({ emitEvent: false });
      this.form.get('parentFirstName')?.enable({ emitEvent: false });
      this.form.get('parentLastName')?.enable({ emitEvent: false });
    }
  }

  private safeParseJson(value: string): unknown {
    try {
      return JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }

  private handleEndorsementDecisionChange(decision: string): void {
    const firstNameControl = this.form.get('parentFirstName');
    const lastNameControl = this.form.get('parentLastName');
    const refusalReasonControl = this.form.get('refusalReason');
    const refusalReasonOtherControl = this.form.get('refusalReasonOther');

    if (decision === 'refuse') {
      // Refusal selected: require refusal reason, update parent name validators
      refusalReasonControl?.setValidators([Validators.required]);
      this.updateParentNameValidators();
    } else if (decision === 'endorse') {
      // Endorsement selected: clear refusal fields, update parent name validators
      refusalReasonControl?.clearValidators();
      refusalReasonOtherControl?.clearValidators();
      refusalReasonControl?.setValue(null, { emitEvent: false });
      refusalReasonOtherControl?.setValue(null, { emitEvent: false });
      this.updateParentNameValidators();
    } else {
      // No decision: clear all validators
      firstNameControl?.clearValidators();
      lastNameControl?.clearValidators();
      refusalReasonControl?.clearValidators();
      refusalReasonOtherControl?.clearValidators();
    }

    firstNameControl?.updateValueAndValidity({ emitEvent: false });
    lastNameControl?.updateValueAndValidity({ emitEvent: false });
    refusalReasonControl?.updateValueAndValidity({ emitEvent: false });
    refusalReasonOtherControl?.updateValueAndValidity({ emitEvent: false });
  }

  private handleRefusalReasonChange(reason: string): void {
    const refusalReasonOtherControl = this.form.get('refusalReasonOther');

    if (reason === 'Other') {
      refusalReasonOtherControl?.setValidators([Validators.required]);
    } else {
      refusalReasonOtherControl?.clearValidators();
      refusalReasonOtherControl?.setValue(null, { emitEvent: false });
    }

    refusalReasonOtherControl?.updateValueAndValidity({ emitEvent: false });
  }

  private updateParentNameValidators(): void {
    const firstNameControl = this.form.get('parentFirstName');
    const lastNameControl = this.form.get('parentLastName');

    const validators = [
      Validators.required,
      this.createParentNameValidator('first')
    ];

    const lastNameValidators = [
      Validators.required,
      this.createParentNameValidator('last')
    ];

    firstNameControl?.setValidators(validators);
    lastNameControl?.setValidators(lastNameValidators);
    firstNameControl?.updateValueAndValidity({ emitEvent: false });
    lastNameControl?.updateValueAndValidity({ emitEvent: false });
  }

  private createParentNameValidator(nameType: 'first' | 'last') {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      const rosterValue = nameType === 'first' ? this.rosterParentFirstName : this.rosterParentLastName;
      
      if (!rosterValue) {
        return null; // No roster value to compare against
      }

      const inputValue = control.value.trim().toLowerCase();
      const rosterValueLower = rosterValue.trim().toLowerCase();

      if (inputValue !== rosterValueLower) {
        const nameLabel = nameType === 'first' ? 'First' : 'Last';
        return { 
          nameMismatch: { 
            expected: rosterValue, 
            actual: control.value,
            message: `${nameLabel} name must match: ${rosterValue}`
          } 
        };
      }

      return null;
    };
  }

  protected submitForm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const isAccepted = this.form.value.endorsementDecision === 'endorse';
    
    this.submitFormData.emit(
      JSON.stringify({
        ...this.form.getRawValue(), 
        accepted: isAccepted
      })
    );
  }

  protected isFormReady(): boolean {
    return this.form.valid && this.form.dirty;
  }

  public onBack(): void {
    this.router.navigate(['/']).then();
  }

  protected formatCurrency(value: number | null): string {
    if (value == null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  }

  protected getTermDisplayValue(): string {
    const semester = this.form.get('semester')?.value || '';
    const termStartDate = this.form.get('termStartDate')?.value;
    
    if (!termStartDate) {
      return semester;
    }
    
    const year = new Date(termStartDate).getFullYear();
    return `${semester} ${year}`;
  }

  protected getRefusalReasons(): string[] {
    const schoolName = this.form.get('schoolName')?.value || '[School Name]';
    return [
      `My student is not attending ${schoolName}.`,
      `The tuition costs reported by ${schoolName} is incorrect.`,
      'Other'
    ];
  }

  protected isRefusalSelected(): boolean {
    return this.form.get('endorsementDecision')?.value === 'refuse';
  }

  protected isOtherReasonSelected(): boolean {
    return this.form.get('refusalReason')?.value === 'Other';
  }
}
